import html2canvas from 'html2canvas';

/**
 * Renders a live part of the page to a canvas.
 * Extracted from the screenshot button, which was the only caller until the PDF reports needed the
 * same thing: a module whose result is a map has to put that map in its report, and the map is a
 * Google Maps overlay rather than an image with a URL, so the only way to get it is to render it.
 * Shared rather than copied because what is in here is not obvious - the tile seams, the inner
 * shadows and the fractional device pixel ratio each cost a measurement to find - and a second
 * copy would be a second place for those findings to rot. CSqueeze and EUSCoast are what this
 * codebase already learned that from.
 * @module CAPTURE_ELEMENT
 */

/**
 * @constant APP_ROOT_SELECTOR
 * @description Element captured by the screenshot. The app's own root rather than `document.body`,
 * so the image holds the application exactly as laid out - map, overlay panels and navigation bar -
 * with none of the browser's own chrome around it.
 * @memberof module:CAPTURE_ELEMENT
 */
export const APP_ROOT_SELECTOR = '#root';

/**
 * @function captureScale
 * @description Device pixel ratio rounded to a whole number. Windows display scaling commonly
 * reports fractional ratios (1.25, 1.5); handing one of those to `html2canvas` makes every element
 * land on a fractional canvas pixel, and the antialiasing of those edges reads as a faint outline
 * around each card. A whole-number scale keeps the capture sharp without the seams.
 * @memberof module:CAPTURE_ELEMENT
 * @returns {Number} A capture scale of at least 1.
 */
const captureScale = () => Math.max(1, Math.round(window.devicePixelRatio || 1));

/**
 * @function pageBackgroundColor
 * @description The page's own background colour, for `html2canvas` to paint under the capture.
 * Capturing onto a transparent canvas instead leaves a dark fringe wherever a rounded corner is
 * antialiased against nothing, which shows up as a border on every panel.
 * @memberof module:CAPTURE_ELEMENT
 * @returns {String} A CSS colour.
 */
const pageBackgroundColor = () => {
  const { backgroundColor } = window.getComputedStyle(document.body);
  const isTransparent = !backgroundColor || backgroundColor === 'transparent' || backgroundColor === 'rgba(0, 0, 0, 0)';
  return isTransparent ? '#ffffff' : backgroundColor;
};

/**
 * @constant MIN_TILE_SIZE
 * @description Smallest rendered size, in px, at which an image inside the map is taken for a map
 * tile. Google serves 256px tiles; markers and control glyphs are far smaller and must keep their
 * exact dimensions.
 * @memberof module:CAPTURE_ELEMENT
 */
const MIN_TILE_SIZE = 200;

/**
 * @constant CAPTURE_STYLES
 * @description Stylesheet injected into the cloned document only, never into the page.
 * Hides the overlay columns' scrollbars, which html2canvas would otherwise paint into the image as
 * bars, and drops every shadow: the library lays those out inside the element rather than around
 * it, so each panel came out ringed by a dark inner rim about as wide as the shadow's blur.
 * Measuring a card's edge in a capture shows the rim is darker along the bottom than along the top,
 * matching the downward offset of Material-UI's elevation shadow. Panels are meant to read as flat
 * blocks of colour in a still image, and the universal selector is what makes that hold: the
 * shadows come from Material-UI's elevation on some panels and from plain `boxShadow` rules on
 * others - the bathymetry legend, MepBay's floating windows, the collapse toggles - so matching
 * component classes would have left those ringed.
 * @memberof module:CAPTURE_ELEMENT
 */
const CAPTURE_STYLES = [
  '*::-webkit-scrollbar { width: 0 !important; height: 0 !important; }',
  '* { box-shadow: none !important; }',
].join(' ');

/**
 * @function closeTileSeams
 * @description Grows each map tile by a pixel in the cloned document, so neighbouring tiles overlap
 * instead of leaving a gap between them.
 * Google Maps lays its tiles out at sub-pixel offsets. html2canvas rounds each one independently
 * as it draws, and where two roundings disagree a one-pixel gap opens along the seam; what shows
 * through is the page background, which is light because the dark palette only applies inside the
 * module. That is the grid of lines across the captured map, and it is measurable: the seams fall
 * exactly 256px apart - Google's tile size - and their colour is the map colour blended with the
 * page background at a constant alpha.
 * Overlapping the tiles closes the gap with neighbouring map content rather than with a flat
 * colour, so it holds whatever the palette or the base layer is. One pixel on 256 is a 0.4%
 * stretch, below what the eye resolves.
 * @param {Document} clonedDocument - The offscreen document html2canvas will render.
 * **Called by:
 * @see {ScreenshotButton} - through html2canvas's `onclone` hook.
 * @memberof module:CAPTURE_ELEMENT
 * @returns {void}
 */
const closeTileSeams = (clonedDocument) => {
  clonedDocument.querySelectorAll('.gm-style img').forEach((tile) => {
    const width = tile.width || parseFloat(tile.style.width);
    const height = tile.height || parseFloat(tile.style.height);
    if (!(width >= MIN_TILE_SIZE) || !(height >= MIN_TILE_SIZE)) return;

    tile.style.width = `${Math.ceil(width) + 1}px`;
    tile.style.height = `${Math.ceil(height) + 1}px`;
  });
};

/**
 * @function captureElement
 * @description Renders one element to a canvas, offscreen, without changing what is on screen.
 * `useCORS` lets the capture include the Google Maps tiles, which are served cross-origin; a tile
 * the browser refuses to release still comes out blank, which is a limit of capturing third-party
 * imagery from the page rather than something this function can work around.
 * @param {Element} element - What to render.
 * @memberof module:CAPTURE_ELEMENT
 * @returns {Promise<Object>} The canvas.
 */
export const captureElement = (element) =>
  html2canvas(element, {
    useCORS: true,
    allowTaint: false,
    logging: false,
    scale: captureScale(),
    backgroundColor: pageBackgroundColor(),
    onclone: (clonedDocument) => {
      const style = clonedDocument.createElement('style');
      style.textContent = CAPTURE_STYLES;
      clonedDocument.head.appendChild(style);
      closeTileSeams(clonedDocument);
    },
  });

/**
 * @function captureAppRoot
 * @description Renders the whole application - map, overlay panels and navigation bar - with none
 * of the browser's own chrome around it.
 * @memberof module:CAPTURE_ELEMENT
 * @returns {Promise<Object|null>} The canvas, or `null` when the root is not in the document.
 */
export const captureAppRoot = () => {
  const root = document.querySelector(APP_ROOT_SELECTOR);
  return root ? captureElement(root) : Promise.resolve(null);
};

/**
 * @constant MAP_SETTLE_TIMEOUT
 * @description How long to wait for a map to finish moving and drawing before capturing it, in ms.
 * Google Maps fires `idle` once it has stopped panning and zooming, but the tiles for the new view
 * are still arriving at that point, so a short wait follows it. The timeout is the ceiling: a map
 * that never settles - no network, no tiles - must not hold the report forever.
 * @memberof module:CAPTURE_ELEMENT
 */
export const MAP_SETTLE_TIMEOUT = 4000;

/**
 * @constant MAP_TILE_GRACE
 * @description Extra wait after `idle`, in ms, for the tiles of the new view to arrive.
 * @memberof module:CAPTURE_ELEMENT
 */
export const MAP_TILE_GRACE = 600;

/**
 * @function settleMapForCapture
 * @description Frames a map on an area and waits for it to stop moving, so a capture taken next
 * shows that area rather than wherever the map happened to be left.
 * This exists because the map in a report is captured from the screen: whatever the user had
 * panned or zoomed to is what would come out, and a report whose map shows the sea next to the
 * analysis is worse than one with no map at all. Framing it first makes the image answer the
 * question the report is about.
 * Resolves either way - a map that cannot be framed is still worth capturing as it is.
 * @param {Object} map - The Google Maps instance.
 * @param {Object} [bounds] - A `LatLngBounds` to frame, or a function that fits the map itself.
 * @memberof module:CAPTURE_ELEMENT
 * @returns {Promise<void>}
 */
export const settleMapForCapture = (map, bounds) =>
  new Promise((resolve) => {
    if (!map || !bounds) {
      resolve();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const ceiling = setTimeout(finish, MAP_SETTLE_TIMEOUT);

    try {
      const listener = window.google?.maps?.event?.addListenerOnce(map, 'idle', () => {
        setTimeout(() => {
          clearTimeout(ceiling);
          finish();
        }, MAP_TILE_GRACE);
      });
      if (typeof bounds === 'function') bounds();
      else map.fitBounds(bounds);
      if (!listener) {
        clearTimeout(ceiling);
        setTimeout(finish, MAP_TILE_GRACE);
      }
    } catch (error) {
      console.error('[captureElement] could not frame the map before capturing', error);
      clearTimeout(ceiling);
      finish();
    }
  });
